import { mergician } from 'mergician';

const DEFAULT_LOCALE = "en-US";
const normalizeEntities = (entities, { mapFn = (x) => x, filterFn = Boolean, }) => {
    if (!entities)
        return undefined;
    const filtered = entities.filter(filterFn);
    if (filtered.length === 0)
        return undefined;
    return filtered.map(mapFn);
};
const getName = (x) => x.name;
const checkTimeDimension = (timeDimension) => Boolean(timeDimension &&
    timeDimension.dimension &&
    (timeDimension.granularity || timeDimension.dateRange));
const validateOrderBy = (orderByParam, dimensions, measures, timeDimensions) => {
    const unknownDimensionsOrMeasures = [];
    const dimensionAndMeasureNames = [
        ...[...measures, ...dimensions].filter(Boolean).map((x) => x.name),
        ...timeDimensions
            .filter(Boolean)
            .map((x) => x.dimension)
            .filter(Boolean),
    ];
    for (const orderBy of orderByParam) {
        const { name } = orderBy.property;
        if (dimensionAndMeasureNames.includes(name))
            continue;
        unknownDimensionsOrMeasures.push(name);
    }
    return unknownDimensionsOrMeasures;
};
const createBuiltInType = (name, typeConfig = {}) => ({
    __embeddableType: "built-in",
    toString: () => name,
    typeConfig: {
        label: name,
        optionLabel: () => name,
        ...typeConfig,
    },
});
const validateFilter = (filter) => {
    if (!filter.property)
        return "filter property must be set";
    if (!filter.operator)
        return "filter operator must be set";
};
const convertFilterValue = (value) => {
    // no need to convert these values
    if (value == null ||
        typeof value === "string" ||
        typeof value === "boolean" ||
        typeof value === "number" ||
        value instanceof Date ||
        Array.isArray(value)) {
        return value;
    }
    if (Object.hasOwn(value, "date"))
        return value?.date;
    const timeRange = value;
    if (timeRange?.from && timeRange?.to) {
        return [timeRange.from, timeRange.to];
    }
    // time or timeRange must be converted
    if (timeRange?.relativeTimeString)
        return timeRange.relativeTimeString;
};
function deduplicateEntitiesByName(entities) {
    if (!entities || entities.length === 0)
        return [];
    const seen = new Set();
    return entities.filter((entity) => {
        if (seen.has(entity.name)) {
            return false;
        }
        seen.add(entity.name);
        return true;
    });
}

const DIMENSION_TYPE = "dimension";
const MEASURE_TYPE = "measure";
const deepFlattenSelect = (input) => {
    if (Array.isArray(input)) {
        return input.flatMap(deepFlattenSelect);
    }
    return [input];
};
const processSelectParam = (selectParam) => {
    const result = {
        dimensions: [],
        measures: [],
        timeDimensions: [],
    };
    if (!selectParam) {
        return result;
    }
    const flat = deepFlattenSelect(selectParam).filter(Boolean);
    if (!flat.length) {
        result.error = "select must not be empty";
        return result;
    }
    for (const ent of flat) {
        const isValidEntity = typeof ent === "object" &&
            (isDimensionOrMeasure(ent) || isTimeDimension(ent));
        if (!isValidEntity) {
            result.error =
                "select must only contain Dimension, Measure or TimeDimension objects";
            return result;
        }
        if (isTimeDimension(ent)) {
            result.timeDimensions.push(ent);
        }
        else if (isDimension(ent)) {
            const dimension = ent;
            const granularity = dimension.inputs?.granularity;
            if (dimension.nativeType === "time" && granularity) {
                result.timeDimensions.push({
                    dimension: dimension.name,
                    granularity,
                    title: dimension.title,
                });
            }
            else {
                result.dimensions.push(dimension);
            }
        }
        else if (isMeasure(ent)) {
            result.measures.push(ent);
        }
        else {
            const type = "__type__" in ent ? ent.__type__ : typeof ent;
            result.error = `Unknown __type__ in select: ${type}`;
            return result;
        }
    }
    return result;
};
function validateLegacyFields(request) {
    if (request.measures?.length > 0 &&
        request.measures.some((m) => m?.__type__ !== MEASURE_TYPE)) {
        return "Unexpected type passed to `measures` in loadData function.  Expected an array of type Measure.";
    }
    if (request.dimensions?.length > 0 &&
        request.dimensions.some((m) => m?.__type__ !== DIMENSION_TYPE)) {
        return "Unexpected type passed to `dimensions` in loadData function.  Expected an array of type Dimension.";
    }
    return null;
}
function processApiSelection(request) {
    const result = {
        dimensions: [],
        measures: [],
        timeDimensions: [],
        usingSelect: false,
    };
    if (request.select) {
        result.usingSelect = true;
        const selectResult = processSelectParam(request.select);
        if (selectResult.error) {
            result.error = selectResult.error;
            return result;
        }
        result.dimensions = selectResult.dimensions;
        result.measures = selectResult.measures;
        result.timeDimensions = selectResult.timeDimensions;
    }
    else {
        const legacyError = validateLegacyFields(request);
        if (legacyError) {
            result.error = legacyError;
            return result;
        }
        result.dimensions =
            normalizeEntities(request.dimensions, { mapFn: (x) => x }) ?? [];
        result.measures =
            normalizeEntities(request.measures, { mapFn: (x) => x }) ?? [];
        result.timeDimensions =
            normalizeEntities(request.timeDimensions, { filterFn: Boolean }) ?? [];
    }
    result.dimensions = deduplicateEntitiesByName(result.dimensions);
    result.measures = deduplicateEntitiesByName(result.measures);
    return result;
}
function validateOrderByLogic(orderBy, dimensions, measures, timeDimensions) {
    const unknownDimensionsOrMeasures = validateOrderBy(orderBy ?? [], dimensions, measures, timeDimensions);
    if (unknownDimensionsOrMeasures.length > 0) {
        return `Cannot order by ${unknownDimensionsOrMeasures.join(", ")} as no such ${unknownDimensionsOrMeasures.length === 1 ? "property" : "properties"} has been loaded.`;
    }
    return null;
}
function validateFiltersLogic(filters) {
    if (filters) {
        const errors = filters
            .map((f, index) => ({
            index,
            message: validateFilter(f),
        }))
            .filter((e) => !!e.message);
        if (errors.length) {
            return errors
                .map((e) => `filter[${e.index}] is not valid: ${e.message}`)
                .join("\n");
        }
    }
    return null;
}
function buildQueryObject(request, dimensions, measures, timeDimensions, order) {
    return {
        inputName: request.from.inputName,
        datasetId: request.from.datasetId,
        embeddableId: request.from.embeddableId,
        dimensions: normalizeEntities(dimensions, { mapFn: getName }) ?? [],
        measures: normalizeEntities(measures, { mapFn: getName }) ?? [],
        order,
        timeDimensions: normalizeEntities(timeDimensions, { filterFn: checkTimeDimension }) ?? [],
        offset: request.offset,
        limit: request.limit,
        variableValues: request.from.variableValues,
        filters: request.filters?.map((f) => ({
            member: f.property.name,
            operator: f.operator,
            values: convertFilterValue(f.value),
        })),
        timezone: request.timezone,
        countRows: request.countRows,
    };
}

const LOAD_DATA_EVENT = "embeddable-event:load-data";
const CUBE_MEASURE_TYPE_STRING = "string";
const CUBE_MEASURE_TYPE_TIME = "time";
const CUBE_MEASURE_TYPE_BOOLEAN = "boolean";
const CUBE_MEASURE_TYPE_NUMBER = "number";
const CUBE_MEASURE_TYPE_COUNT = "count";
const CUBE_MEASURE_TYPE_COUNT_DISTINCT = "count_distinct";
const CUBE_MEASURE_TYPE_COUNT_DISTINCT_APPROX = "count_distinct_approx";
const CUBE_MEASURE_TYPE_SUM = "sum";
const CUBE_MEASURE_TYPE_AVG = "avg";
const CUBE_MEASURE_TYPE_MIN = "min";
const CUBE_MEASURE_TYPE_MAX = "max";
const CUBE_DIMENSION_TYPE_STRING = "string";
const CUBE_DIMENSION_TYPE_NUMBER = "number";
const CUBE_DIMENSION_TYPE_BOOLEAN = "boolean";
const CUBE_DIMENSION_TYPE_GEO = "geo";
const CUBE_DIMENSION_TYPE_TIME = "time";
const NativeDataType = {
    string: "string",
    number: "number",
    time: "time",
    boolean: "boolean",
};
const FilterOperator = {
    equals: "equals",
    notEquals: "notEquals",
    contains: "contains",
    notContains: "notContains",
    startsWith: "startsWith",
    endsWith: "endsWith",
    gt: "gt",
    gte: "gte",
    lt: "lt",
    lte: "lte",
    notNull: "set",
    isNull: "notSet",
    inDateRange: "inDateRange",
    notInDateRange: "notInDateRange",
    beforeDate: "beforeDate",
    afterDate: "afterDate",
    measureFilter: "measureFilter",
};
const isLoadDataParams = (ldp) => typeof ldp === "object" &&
    ldp &&
    "requestParams" in ldp &&
    "dataLoader" in ldp;
const executeDataRequest = (triggerElement, request, componentId, propertyName) => {
    if (!request.from)
        return "No dataset selected";
    // Mutually exclusive API check
    if (request.select &&
        (request.dimensions || request.measures || request.timeDimensions)) {
        return "loadData expects you to use either dimensions, timeDimensions and measures, or select. You cannot use both together.";
    }
    // Process select or legacy fields
    const apiResult = processApiSelection(request);
    if (apiResult.error) {
        return apiResult.error;
    }
    const { dimensions, measures, timeDimensions } = apiResult;
    // Validate required fields
    const dimensionsOrMeasuresDefined = dimensions.length || measures.length || timeDimensions.length;
    if (!dimensionsOrMeasuresDefined) {
        return "At least a dimension or a measure should be selected.";
    }
    // Order By Validation
    const orderByError = validateOrderByLogic(request.orderBy, dimensions, measures, timeDimensions);
    if (orderByError) {
        return orderByError;
    }
    // Filter Validation
    const filterError = validateFiltersLogic(request.filters);
    if (filterError) {
        return filterError;
    }
    // Prepare OrderBy for query
    const order = normalizeEntities(request.orderBy, {
        mapFn: (o) => [o.property.name, o.direction],
    });
    // Build Query Object
    const query = buildQueryObject(request, dimensions, measures, timeDimensions, order);
    // Dispatch Event
    triggerElement.dispatchEvent(new CustomEvent(LOAD_DATA_EVENT, {
        bubbles: true,
        composed: true,
        detail: {
            query,
            componentId,
            propertyName,
        },
    }));
};
const loadData = (requestParams) => ({
    requestParams,
    dataLoader: executeDataRequest,
});
const isDimension = (dimensionOrMeasure) => dimensionOrMeasure?.__type__ === DIMENSION_TYPE;
const isMeasure = (dimensionOrMeasure) => dimensionOrMeasure?.__type__ === MEASURE_TYPE;
const isTimeDimension = (dimensionOrMeasure) => "dimension" in (dimensionOrMeasure ?? {}) &&
    !("__type__" in dimensionOrMeasure);
const isDimensionOrMeasure = (dimensionOrMeasure) => isDimension(dimensionOrMeasure) || isMeasure(dimensionOrMeasure);

const getOperationObject = (operation, value) => ({
    operation,
    value: value ?? null,
    __embeddableVariableMeta: true,
});
const Value = {
    noFilter: () => getOperationObject("NO_FILTER"),
    of: (value) => getOperationObject("VALUE", value),
};

const UPDATE_VALUE_EVENT = "embeddable:value:changed";
const setValue = (triggerElement, value, componentId, eventName) => {
    const event = new CustomEvent(UPDATE_VALUE_EVENT, {
        bubbles: true,
        composed: true,
        detail: {
            componentId,
            value,
            eventName,
        },
    });
    triggerElement.dispatchEvent(event);
};

// Helper to get the bundleHash from window if available
const getBundleHash$1 = () => globalThis.__EMBEDDABLE_BUNDLE_HASH__ ?? null;
const defineType = (typeName, typeConfig) => {
    if (ALL_NATIVE_TYPES.includes(typeName)) {
        throw new Error(`Type ${typeName} is part of the nativeTypes and cannot be defined`, { cause: "sdk" });
    }
    const type = {
        __embeddableType: "custom",
        toString: () => typeName,
        typeConfig,
    };
    // Get the bundleHash from stored value or window
    const bundleHash = getBundleHash$1();
    globalThis.__EMBEDDABLE__ = globalThis.__EMBEDDABLE__ || {};
    const embeddable = bundleHash
        ? (globalThis.__EMBEDDABLE__[bundleHash] =
            globalThis.__EMBEDDABLE__[bundleHash] || {})
        : globalThis.__EMBEDDABLE__;
    embeddable.types = embeddable.types || {};
    // Preserve existing types by merging with new type
    embeddable.types = {
        ...embeddable.types,
        [typeName]: {
            name: typeName,
            ...typeConfig,
        },
    };
    return type;
};
const STRING = "string";
const NUMBER = "number";
const BOOLEAN = "boolean";
const TIME = "time";
const TIME_RANGE = "timeRange";
const GRANULARITY = "granularity";
const DATASET = "dataset";
const MEASURE = "measure";
const DIMENSION = "dimension";
const DIMENSION_OR_MEASURE = "dimensionOrMeasure";
const FILTERS = "filters";
const DEFAULT_NATIVE_TYPES = [
    STRING,
    NUMBER,
    BOOLEAN,
    TIME,
    TIME_RANGE,
    GRANULARITY,
    FILTERS,
];
const ALL_NATIVE_TYPES = [
    ...DEFAULT_NATIVE_TYPES,
    DATASET,
    MEASURE,
    DIMENSION,
    DIMENSION_OR_MEASURE,
];
const MEASURE_TYPE_STRING = "string";
const MEASURE_TYPE_TIME = "time";
const MEASURE_TYPE_BOOLEAN = "boolean";
const MEASURE_TYPE_NUMBER = "number";
const MEASURE_TYPE_COUNT = "count";
const MEASURE_TYPE_COUNT_DISTINCT = "count_distinct";
const MEASURE_TYPE_COUNT_DISTINCT_APPROX = "count_distinct_approx";
const MEASURE_TYPE_SUM = "sum";
const MEASURE_TYPE_AVG = "avg";
const MEASURE_TYPE_MIN = "min";
const MEASURE_TYPE_MAX = "max";
const MEASURE_TYPES = [
    MEASURE_TYPE_STRING,
    MEASURE_TYPE_TIME,
    MEASURE_TYPE_BOOLEAN,
    MEASURE_TYPE_NUMBER,
    MEASURE_TYPE_COUNT,
    MEASURE_TYPE_COUNT_DISTINCT,
    MEASURE_TYPE_COUNT_DISTINCT_APPROX,
    MEASURE_TYPE_SUM,
    MEASURE_TYPE_AVG,
    MEASURE_TYPE_MIN,
    MEASURE_TYPE_MAX,
];
const DIMENSION_TYPE_STRING = "string";
const DIMENSION_TYPE_NUMBER = "number";
const DIMENSION_TYPE_BOOLEAN = "boolean";
const DIMENSION_TYPE_GEO = "geo";
const DIMENSION_TYPE_TIME = "time";
const DIMENSION_TYPES = [
    DIMENSION_TYPE_STRING,
    DIMENSION_TYPE_NUMBER,
    DIMENSION_TYPE_BOOLEAN,
    DIMENSION_TYPE_GEO,
    DIMENSION_TYPE_TIME,
];
const DIMENSION_OR_MEASURE_TYPES = [
    ...DIMENSION_TYPES,
    ...MEASURE_TYPES,
];

// Helper to get the bundleHash from window if available
// Check if we have a bundleHash in the window
const getBundleHash = () => globalThis.__EMBEDDABLE_BUNDLE_HASH__ ?? null;
const defineOption = (type, option) => {
    // Get the bundleHash from stored value or window
    const bundleHash = getBundleHash();
    globalThis.__EMBEDDABLE__ ??= {};
    const embeddable = bundleHash
        ? (globalThis.__EMBEDDABLE__[bundleHash] ??= {})
        : globalThis.__EMBEDDABLE__;
    if (typeof type === "string") {
        // Handle native types
        embeddable.nativeTypes = embeddable.nativeTypes || {};
        embeddable.nativeTypes[type] = embeddable.nativeTypes[type] || {};
        embeddable.nativeTypes[type].options ??= [];
        embeddable.nativeTypes[type].options.push(option);
    }
    else {
        // Handle custom types
        const typeName = type.toString();
        if (!embeddable?.types?.[typeName])
            return;
        embeddable.types[typeName].options ??= [];
        embeddable.types[typeName].options.push(option);
    }
};

const StringType = createBuiltInType("string", {
    transform: (value) => value,
    optionLabel: (value) => Array.isArray(value)
        ? `[${value.map((v) => `"${v}"`).join(",")}]`
        : `"${value}"`,
});
const NumberType = createBuiltInType("number", {
    transform: (value) => Array.isArray(value) ? value : value ? Number(value) : value,
    optionLabel: (value) => Array.isArray(value)
        ? `[${value.join(",")}]`
        : (value?.toLocaleString(DEFAULT_LOCALE) ?? ""),
});
const BooleanType = createBuiltInType("boolean", {
    transform: (value) => value === "true" || value === true,
    optionLabel: (value) => (value ? "true" : "false"),
});
const TimeType = createBuiltInType("time", {
    transform: (value) => {
        const date = value?.date ? new Date(value.date) : undefined;
        const isValid = date && date.toString() !== "Invalid Date";
        return {
            date: isValid ? date : undefined,
            relativeTimeString: value?.relativeTimeString,
        };
    },
    optionLabel: (value) => {
        if (!value)
            return "";
        if (value?.date) {
            return (value.date?.toLocaleDateString(DEFAULT_LOCALE) ??
                value.date.toLocaleString());
        }
        return value.relativeTimeString;
    },
});
const TimeRangeType = createBuiltInType("timeRange", {
    transform: (value) => {
        // Return undefined instead of a null populated object
        if (!value)
            return undefined;
        const [from, to] = [value?.from, value?.to];
        const fromDate = new Date(from);
        const toDate = new Date(to);
        return {
            from: fromDate.toString() !== "Invalid Date" ? fromDate : undefined,
            to: toDate.toString() !== "Invalid Date" ? toDate : undefined,
            relativeTimeString: value?.relativeTimeString,
        };
    },
    optionLabel: (value) => {
        if (!value)
            return "";
        if (value?.from && value?.to) {
            return `${value.from?.toLocaleDateString(DEFAULT_LOCALE) ??
                value.from?.toLocaleString()},${value.to?.toLocaleDateString(DEFAULT_LOCALE) ??
                value.to?.toLocaleString()}`;
        }
        return value?.relativeTimeString;
    },
});
const GranularityType = createBuiltInType("granularity", {
    transform: (value) => value,
    optionLabel: (value) => value,
});
const DatasetType = createBuiltInType("dataset");
const MeasureType = createBuiltInType("measure");
const DimensionType = createBuiltInType("dimension");
const DimensionOrMeasureType = createBuiltInType("dimensionOrMeasure");

var nativeTypes = /*#__PURE__*/Object.freeze({
    __proto__: null,
    BooleanType: BooleanType,
    DatasetType: DatasetType,
    DimensionOrMeasureType: DimensionOrMeasureType,
    DimensionType: DimensionType,
    GranularityType: GranularityType,
    MeasureType: MeasureType,
    NumberType: NumberType,
    StringType: StringType,
    TimeRangeType: TimeRangeType,
    TimeType: TimeType
});

const defineTheme = (parentTheme, childTheme) => {
    return mergician(parentTheme, childTheme);
};

const mockDataResponse = (columnNames, values) => {
    const data = values.map((row) => {
        const obj = {};
        columnNames.forEach((col, index) => {
            obj[col] = row[index];
        });
        return obj;
    });
    return {
        isLoading: false,
        data,
    };
};
const mockDimension = (name, type, extraOptions) => ({
    ...mockModelMember(name, extraOptions),
    __type__: "dimension",
    nativeType: type,
});
const mockMeasure = (name, type, extraOptions) => ({
    ...mockModelMember(name, extraOptions),
    __type__: "measure",
    nativeType: type,
});
const mockModelMember = (name, extraOptions) => ({
    name,
    title: extraOptions?.title || capitalize(name),
    modelTitle: extraOptions?.modelTitle || capitalize(name),
    description: extraOptions?.description || "",
    ...extraOptions,
});
const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

export { ALL_NATIVE_TYPES, CUBE_DIMENSION_TYPE_BOOLEAN, CUBE_DIMENSION_TYPE_GEO, CUBE_DIMENSION_TYPE_NUMBER, CUBE_DIMENSION_TYPE_STRING, CUBE_DIMENSION_TYPE_TIME, CUBE_MEASURE_TYPE_AVG, CUBE_MEASURE_TYPE_BOOLEAN, CUBE_MEASURE_TYPE_COUNT, CUBE_MEASURE_TYPE_COUNT_DISTINCT, CUBE_MEASURE_TYPE_COUNT_DISTINCT_APPROX, CUBE_MEASURE_TYPE_MAX, CUBE_MEASURE_TYPE_MIN, CUBE_MEASURE_TYPE_NUMBER, CUBE_MEASURE_TYPE_STRING, CUBE_MEASURE_TYPE_SUM, CUBE_MEASURE_TYPE_TIME, DEFAULT_NATIVE_TYPES, DIMENSION_OR_MEASURE_TYPES, DIMENSION_TYPES, FilterOperator, MEASURE_TYPES, NativeDataType, Value, defineOption, defineTheme, defineType, isDimension, isDimensionOrMeasure, isLoadDataParams, isMeasure, isTimeDimension, loadData, mockDataResponse, mockDimension, mockMeasure, nativeTypes, setValue };
